<template>
  <span></span>
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { useMqttStore } from '@/store/mqttStore'
import { storeToRefs } from 'pinia'
import { useDeviceStore } from '@/store/deviceStore'
import {
  antDevice,
  bleDevice,
  deviceType,
  motorStatus,
  motorOvercurrentStatus,
  motorStalledStatus,
  motorTemperatureStatus,
} from '@/types/device'
import { MqttTopic } from '@/mqttDefinitions/topics'

const mqttStore = useMqttStore()
const deviceStore = useDeviceStore()

const { doSubscribe } = mqttStore
const { client } = storeToRefs(mqttStore)

const topics = [
  MqttTopic.DEVICES_ANT_LIST,
  MqttTopic.DEVICES_BLE_LIST,
  MqttTopic.CONTROLLER_HAS_OVERCURRENT,
  MqttTopic.CONTROLLER_HAS_STALLED,
  MqttTopic.CONTROLLER_TEMPERATURE,
  MqttTopic.MOTORCONTROLLER_HANDLEBAR_X_STATUS,
  MqttTopic.MOTORCONTROLLER_HANDLEBAR_Y_STATUS,
  MqttTopic.MOTORCONTROLLER_SADDLE_X_STATUS,
  MqttTopic.MOTORCONTROLLER_SADDLE_Y_STATUS,
]

topics.forEach((topic) => doSubscribe(topic, client.value))

const [
  antDevicesTopic,
  bleDevicesTopic,
  motorOvercurrentTopic,
  motorStalledTopic,
  motorTemperatureTopic,
  handlebarXStatusTopic,
  handlebarYStatusTopic,
  saddleXStatusTopic,
  saddleYStatusTopic,
] = topics

const { getRawValueOnTopic } = storeToRefs(mqttStore)
const { getAntDevicesByType, getBleDevicesByType } = storeToRefs(deviceStore)

const { updateAntList, updateBleList } = deviceStore

const antDevicesList = computed(() => getRawValueOnTopic.value(antDevicesTopic))
const bleDevicesList = computed(() => getRawValueOnTopic.value(bleDevicesTopic))
const motorOvercurrent = computed(() =>
  getRawValueOnTopic.value(motorOvercurrentTopic),
)
const motorStalled = computed(() => getRawValueOnTopic.value(motorStalledTopic))
const motorTemperature = computed(() =>
  getRawValueOnTopic.value(motorTemperatureTopic),
)
const handlebarXStatus = computed(() =>
  getRawValueOnTopic.value(handlebarXStatusTopic),
)
const handlebarYStatus = computed(() =>
  getRawValueOnTopic.value(handlebarYStatusTopic),
)
const saddleXStatus = computed(() =>
  getRawValueOnTopic.value(saddleXStatusTopic),
)
const saddleYStatus = computed(() =>
  getRawValueOnTopic.value(saddleYStatusTopic),
)

const deviceTypeToStatusKey = {
  heartRate: 'heartRateStatus',
  resistanceWheel: 'resistanceWheelStatus',
  xyTool: 'xyToolStatus',
} as const

const deviceTypes = Object.keys(deviceTypeToStatusKey) as Array<
  keyof typeof deviceTypeToStatusKey
>

watch([antDevicesList, bleDevicesList], () => {
  updateAntList(antDevicesList.value as Array<antDevice>)
  updateBleList(bleDevicesList.value as Array<bleDevice>)

  deviceTypes.forEach((typeKey) => {
    const type = deviceType[typeKey]
    const statusKey = deviceTypeToStatusKey[typeKey]

    deviceStore[statusKey].connecting =
      getAntDevicesByType.value(type).some((device) => device.connecting) ||
      getBleDevicesByType.value(type).some((device) => device.connecting)

    deviceStore[statusKey].connected =
      getAntDevicesByType.value(type).some((device) => device.connected) ||
      getBleDevicesByType.value(type).some((device) => device.connected)
  })
})

watch(motorOvercurrent, () => {
  deviceStore.motorStatus.overcurrent =
    typeof motorOvercurrent.value === 'object' &&
    motorOvercurrent.value?.value.toUpperCase() === motorOvercurrentStatus.TRUE
})

watch(motorStalled, () => {
  deviceStore.motorStatus.stalled =
    typeof motorStalled.value === 'object' &&
    motorStalled.value?.value.toUpperCase() === motorStalledStatus.TRUE
})

watch(motorTemperature, () => {
  deviceStore.motorStatus.highTemperature =
    typeof motorTemperature.value === 'object' &&
    motorTemperature.value?.value.toUpperCase() !== motorTemperatureStatus.FALSE

  if (deviceStore.motorStatus.highTemperature) {
    deviceStore.motorStatus.hasHadHighTemperature = true
  }
})

watch(handlebarXStatus, () => {
  deviceStore.motorStatus.handleBar.x.overcurrent =
    typeof handlebarXStatus.value === 'object' &&
    handlebarXStatus.value?.value.toUpperCase() === motorStatus.OVERCURRENT

  deviceStore.motorStatus.handleBar.x.stalled =
    typeof handlebarXStatus.value === 'object' &&
    handlebarXStatus.value?.value.toUpperCase() === motorStatus.STALLED

  deviceStore.motorStatus.handleBar.x.limitSwitch =
    typeof handlebarXStatus.value === 'object' &&
    handlebarXStatus.value?.value.toUpperCase() ===
      motorStatus.LIMIT_SWITCH_ERROR
})

watch(handlebarYStatus, () => {
  deviceStore.motorStatus.handleBar.y.overcurrent =
    typeof handlebarYStatus.value === 'object' &&
    handlebarYStatus.value?.value.toUpperCase() === motorStatus.OVERCURRENT

  deviceStore.motorStatus.handleBar.y.stalled =
    typeof handlebarYStatus.value === 'object' &&
    handlebarYStatus.value?.value.toUpperCase() === motorStatus.STALLED

  deviceStore.motorStatus.handleBar.y.limitSwitch =
    typeof handlebarYStatus.value === 'object' &&
    handlebarYStatus.value?.value.toUpperCase() ===
      motorStatus.LIMIT_SWITCH_ERROR
})

watch(saddleXStatus, () => {
  deviceStore.motorStatus.saddle.x.overcurrent =
    typeof saddleXStatus.value === 'object' &&
    saddleXStatus.value?.value.toUpperCase() === motorStatus.OVERCURRENT

  deviceStore.motorStatus.saddle.x.stalled =
    typeof saddleXStatus.value === 'object' &&
    saddleXStatus.value?.value.toUpperCase() === motorStatus.STALLED

  deviceStore.motorStatus.saddle.x.limitSwitch =
    typeof saddleXStatus.value === 'object' &&
    saddleXStatus.value?.value.toUpperCase() === motorStatus.LIMIT_SWITCH_ERROR
})

watch(saddleYStatus, () => {
  deviceStore.motorStatus.saddle.y.overcurrent =
    typeof saddleYStatus.value === 'object' &&
    saddleYStatus.value?.value.toUpperCase() === motorStatus.OVERCURRENT

  deviceStore.motorStatus.saddle.y.stalled =
    typeof saddleYStatus.value === 'object' &&
    saddleYStatus.value?.value.toUpperCase() === motorStatus.STALLED

  deviceStore.motorStatus.saddle.y.limitSwitch =
    typeof saddleYStatus.value === 'object' &&
    saddleYStatus.value?.value.toUpperCase() === motorStatus.LIMIT_SWITCH_ERROR
})
</script>

<style lang="scss" scoped></style>
